import { datadogRum } from '@datadog/browser-rum';
import Pica from 'pica';
import { isBrowser } from '@utils/isBrowser';

export class ImageFingerprintError extends Error {}
export class HEICConversionError extends Error {}

const convertIfHeic = (image: string, file?: File) => {
	if ((file?.type === 'image/heic' || file?.type === 'image/heif') && isBrowser) {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const heic2any = require('heic2any');
		return heic2any({
			blob: file,
			toType: 'image/jpeg',
		})
			.then((conversionResult: Blob) => {
				return URL.createObjectURL(conversionResult as Blob);
			})
			.catch((e: Error) => {
				if (e.message.includes('ERR_USER Image is already browser readable')) {
					// File extension is wrong, and we should continue with the image as normal
					return Promise.resolve(image);
				}

				datadogRum.addError('HEIC Conversion Error', {
					message: e?.message,
					type: file?.type,
					name: file?.name,
					size: file?.size,
				});

				throw new HEICConversionError(e?.message);
			});
	}

	return Promise.resolve(image);
};

const processImageWithPica = async (args: {
	blobReader: FileReader;
	height: number;
	image: HTMLImageElement;
	reject: (reason?: unknown) => void;
	width: number;
	file?: File;
}) => {
	const { blobReader, file, height, image, reject, width } = args;

	const pica = new Pica({ features: ['js', 'wasm'] });
	const picaCanvas = document.createElement('canvas');
	picaCanvas.width = width;
	picaCanvas.height = height;
	try {
		const result = await pica.resize(image, picaCanvas, {
			unsharpAmount: 160,
			unsharpRadius: 0.6,
			unsharpThreshold: 1,
			alpha: true,
		});
		const blob = await pica.toBlob(result, 'image/jpeg', 0.9);
		blobReader.readAsDataURL(blob);
	} catch (e) {
		if (e?.code === 'ERR_GET_IMAGE_DATA') {
			datadogRum.addError('Browser fingerprinting exception, routing to error page', {
				message: e?.message,
				type: file?.type,
				name: file?.name,
				size: file?.size,
			});
			reject(new ImageFingerprintError('Error with image fingerprinting'));
		} else {
			datadogRum.addError('Pica Processing Error', {
				code: e.code,
				message: e.message,
				type: file?.type,
				name: file?.name,
				size: file?.size,
			});
			reject('Error resizing image');
		}
	}
};

const resizeImage = (rawData: string, file?: File) => {
	return new Promise<string>((resolve, reject) => {
		if (file?.type === 'application/pdf') {
			resolve(rawData);
		}

		const blobReader = new FileReader();

		const image = new Image();
		image.src = rawData;
		image.onload = async () => {
			const longSide = image.width >= image.height ? image.width : image.height;
			const aspectRatio = 800 / longSide; // Magic numbers

			let width;
			let height;
			if (image.width > 800 || image.height > 600) {
				width = image.width * aspectRatio;
				height = image.height * aspectRatio;
			} else {
				width = image.width;
				height = image.height;
			}

			try {
				const offscreenCanvas = new OffscreenCanvas(width, height);
				const offscreenContext = offscreenCanvas.getContext('2d', { alpha: false });
				offscreenContext?.drawImage(image, 0, 0, width, height);
				const blob = await offscreenCanvas.convertToBlob({ type: 'image/jpeg', quality: 0.9 });
				blobReader.readAsDataURL(blob);
			} catch (e) {
				datadogRum.addError('Image Resizing Error', {
					code: e?.code,
					message: e?.message,
					type: file?.type,
					name: file?.name,
					size: file?.size,
				});

				// Fallback on Pica if offscreen canvas image resizing fails
				processImageWithPica({ blobReader, file, height, image, reject, width });
			}
		};

		blobReader.onload = () => {
			resolve(blobReader.result as string);
		};
	});
};

export const processImage = async (image: string, file?: File) => {
	return convertIfHeic(image, file).then((image) => resizeImage(image, file));
};
